import { getStoredAuthToken } from "@/lib/auth-token";
import { getApiBaseUrl } from "@/lib/runtime-config";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type QueryParamValue = string | number | boolean | null | undefined;

type CustomFetchOptions = RequestInit & {
  headers?: HeadersInit;
  method?: HttpMethod;
  params?: Record<string, QueryParamValue>;
};

function buildUrl(path: string, params?: Record<string, QueryParamValue>): string {
  const baseUrl = getApiBaseUrl();
  const target = path.startsWith("http") ? path : `${baseUrl}${path}`;

  if (!params) {
    return target;
  }

  const url = new URL(target, window.location.origin);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

function parseErrorMessage(payload: string): string {
  if (payload.length === 0) {
    return "Unknown error";
  }

  try {
    const json = JSON.parse(payload) as { message?: string; error?: string };
    return json.message ?? json.error ?? payload;
  } catch {
    return payload;
  }
}

export async function customFetch<TData>(
  url: string,
  options?: CustomFetchOptions
): Promise<TData> {
  const normalizedOptions = options ?? {};
  const token = getStoredAuthToken();
  const requestHeaders = new Headers(normalizedOptions.headers);
  const body = normalizedOptions.body;

  if (body && !(body instanceof FormData) && !requestHeaders.has("Content-Type")) {
    requestHeaders.set("Content-Type", "application/json");
  }

  if (token && !requestHeaders.has("Authorization")) {
    requestHeaders.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(buildUrl(url, normalizedOptions.params), {
    ...normalizedOptions,
    headers: requestHeaders,
    credentials: "include",
  });

  const contentType = response.headers.get("content-type") ?? "";

  if (!response.ok) {
    const errorPayload = await response.text();
    throw new ApiError(parseErrorMessage(errorPayload), response.status, response);
  }

  if (response.status === 204) {
    return {
      data: undefined as TData,
      status: response.status,
      headers: response.headers,
    } as TData;
  }

  if (!contentType.includes("application/json")) {
    throw new ApiError("Expected JSON response", response.status, response);
  }

  const responseData = (await response.json()) as TData;

  return {
    data: responseData,
    status: response.status,
    headers: response.headers,
  } as TData;
}

export class ApiError extends Error {
  status: number;
  response: Response;

  constructor(message: string, status: number, response: Response) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.response = response;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
